import { useState, useCallback, useMemo } from "react"
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async"
import {
  FaCoins,
  FaWeight,
  FaChartLine,
  FaStore,
  FaWallet,
  FaCreditCard,
  FaCog,
  FaCalculator,
  FaRedo,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa"

const ZakatCalculator = () => {
    // translation hook (namespace: calculateZakat)
    const { t } = useTranslation("calculateZakat");
    const translation = t('calculateZakat', { returnObjects: true });

  const [assets, setAssets] = useState({
    cash: '',
    gold: '',
    silver: '',
    investments: '',
    business: '',
    other: ''
  });

  const [liabilities, setLiabilities] = useState({
    debts: ''
  });

  const [settings, setSettings] = useState({
    currency: 'USD',
    nisabMethod: 'gold', // 'gold' or 'silver'
    weightUnit: 'tola', // 'gram' or 'tola'
    goldPrice: '',
    silverPrice: ''
  });

  const [showResult, setShowResult] = useState(false)
  const [results, setResults] = useState({
    totalAssets: 0,
    netAssets: 0,
    nisabThreshold: 0,
    zakatDue: 0,
    isZakatDue: false,
  })

  const [isCalculating, setIsCalculating] = useState(false)
  const [inputErrors, setInputErrors] = useState({})

  const constants = useMemo(
    () => ({
      currencies: [
        { code: "USD", symbol: "$", name: "US Dollar" },
        { code: "EUR", symbol: "€", name: "Euro" },
        { code: "GBP", symbol: "£", name: "British Pound" },
        { code: "PKR", symbol: "₨", name: "Pakistani Rupee" },
        { code: "SAR", symbol: "ر.س", name: "Saudi Riyal" },
        { code: "AED", symbol: "د.إ", name: "UAE Dirham" },
      ],
      GRAM_TO_TOLA: 11.664, // 1 tola = 11.664 grams
      SILVER_NISAB_GRAMS: 612.36, // 612.36 grams of silver
      GOLD_NISAB_GRAMS: 87.48, // 87.48 grams of gold
      ZAKAT_RATE: 0.025, // 2.5%
    }),
    [],
  )

  const calculations = useMemo(() => {
    const goldPricePerGram =
      settings.weightUnit === "gram"
        ? Number.parseFloat(settings.goldPrice) || 0
        : (Number.parseFloat(settings.goldPrice) || 0) / constants.GRAM_TO_TOLA

    const silverPricePerGram =
      settings.weightUnit === "gram"
        ? Number.parseFloat(settings.silverPrice) || 0
        : (Number.parseFloat(settings.silverPrice) || 0) / constants.GRAM_TO_TOLA

    const nisabThreshold =
      settings.nisabMethod === "gold"
        ? constants.GOLD_NISAB_GRAMS * goldPricePerGram
        : constants.SILVER_NISAB_GRAMS * silverPricePerGram

    const goldValue =
      settings.weightUnit === "gram"
        ? (Number.parseFloat(assets.gold) || 0) * (Number.parseFloat(settings.goldPrice) || 0)
        : (Number.parseFloat(assets.gold) || 0) * (Number.parseFloat(settings.goldPrice) || 0)

    const silverValue =
      settings.weightUnit === "gram"
        ? (Number.parseFloat(assets.silver) || 0) * (Number.parseFloat(settings.silverPrice) || 0)
        : (Number.parseFloat(assets.silver) || 0) * (Number.parseFloat(settings.silverPrice) || 0)

    const totalAssets =
      (Number.parseFloat(assets.cash) || 0) +
      goldValue +
      silverValue +
      (Number.parseFloat(assets.investments) || 0) +
      (Number.parseFloat(assets.business) || 0) +
      (Number.parseFloat(assets.other) || 0)

    return { nisabThreshold, totalAssets }
  }, [assets, settings, constants])

  const handleAssetChange = useCallback((field, value) => {
    setAssets((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSettingChange = useCallback((field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleLiabilityChange = useCallback((field, value) => {
    setLiabilities((prev) => ({ ...prev, [field]: value }))
  }, [])

  const getCurrencySymbol = useCallback(() => {
    return constants.currencies.find((c) => c.code === settings.currency)?.symbol || "$"
  }, [settings.currency, constants.currencies])

  const formatCurrency = useCallback(
    (amount) => {
      return `${getCurrencySymbol()}${amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
    [getCurrencySymbol],
  )

  const validateInputs = useCallback(() => {
    const errors = {}

    if (!settings.goldPrice || Number.parseFloat(settings.goldPrice) <= 0) {
      errors.goldPrice = translation.validation.required || "Gold price is required"
    }
    if (!settings.silverPrice || Number.parseFloat(settings.silverPrice) <= 0) {
      errors.silverPrice = translation.validation.required || "Silver price is required"
    }

    setInputErrors(errors)
    return Object.keys(errors).length === 0
  }, [settings.goldPrice, settings.silverPrice, translation.validation])

  const calculateZakat = useCallback(async () => {
    if (!validateInputs()) return

    setIsCalculating(true)

    // Simulate calculation delay for better UX
    await new Promise((resolve) => setTimeout(resolve, 800))

    const totalAssets = calculations.totalAssets
    const totalLiabilities = Number.parseFloat(liabilities.debts) || 0
    const netAssets = totalAssets - totalLiabilities
    const nisabThreshold = calculations.nisabThreshold
    const isZakatDue = netAssets >= nisabThreshold
    const zakatDue = isZakatDue ? netAssets * constants.ZAKAT_RATE : 0

    setResults({
      totalAssets,
      netAssets,
      nisabThreshold,
      zakatDue,
      isZakatDue,
    })
    setIsCalculating(false)
    setShowResult(true)
  }, [calculations, liabilities.debts, constants.ZAKAT_RATE, validateInputs])

  const resetCalculator = useCallback(() => {
    setShowResult(false)
    setIsCalculating(false)
    setInputErrors({})
    setAssets({
      cash: '',
      gold: '',
      silver: '',
      investments: '',
      business: '',
      other: ''
    });
    setLiabilities({
      debts: ''
    });
    setResults({
      totalAssets: 0,
      netAssets: 0,
      nisabThreshold: 0,
      zakatDue: 0,
      isZakatDue: false,
    })
  }, [])


    return (
    <>
      <Helmet>
        <title>{translation.meta?.title || translation.heading || "Calculate Zakat"}</title>
        <meta
          name="description"
          content={translation.meta?.description || "Easily calculate your Zakat online with complete privacy. Use our accurate Zakat calculator for cash, gold, silver, land, business, and investments. Get Nisab-based calculations with today's gold and silver rates, customized for your location."}
        />
        <meta
          name="keywords"
          content={translation.meta?.keywords || "Zakat Calculator, Online Zakat Calculator, Zakat Calculation Tool, Calculate Zakat Online, Zakat on Cash Calculator, Zakat on Gold Calculator, Zakat on Silver Calculator"}
        />
        <meta name="author" content={translation.meta?.author || "AddaZakat Team"} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <main className="flex items-center min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:via-emerald-900 dark:to-teal-900 p-6 md:p-12 lg:p-16 mt-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16">

            <header className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4">
                {translation.heading}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                {translation.subheading}
              </p>
            </header>

            <section
              className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/50 dark:to-teal-900/50 rounded-xl p-8 my-8 border border-emerald-200 dark:border-emerald-700 shadow-lg"
              aria-labelledby="zakat-info"
            >
              <h3
                id="zakat-info"
                className="text-xl font-bold text-neutral-950 dark:text-emerald-200 mb-4 flex items-center"
              >
                <FaCheckCircle className="mr-3 text-emerald-600" />
                {translation.about?.title || t('calculateZakat.results.title') || "About Zakat"}
              </h3>
              <div className="text-neutral-950 dark:text-emerald-300 grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">•</span>
                  <span>{translation.about?.points?.p1 || "Zakat is 2.5% of wealth above the nisab threshold"}</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">•</span>
                  <span>{translation.about?.points?.p2 || "Nisab is the minimum wealth threshold for Zakat obligation"}</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">•</span>
                  <span>{translation.about?.points?.p3 || "Gold nisab: 87.48 grams | Silver nisab: 612.36 grams"}</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="text-emerald-500 mt-1">•</span>
                  <span>{translation.about?.points?.p4 || "Wealth must be held for one lunar year (Hawl)"}</span>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <section className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <FaCog className="mr-3 text-emerald-600" aria-hidden="true" />
                    {translation.settings?.title || "Settings"}
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <label
                        htmlFor="currency-select"
                        className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {translation.settings?.currency || "Currency"}
                      </label>
                      <select
                        id="currency-select"
                        value={settings.currency}
                        onChange={(e) => handleSettingChange("currency", e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:border-emerald-300"
                      >
                        {constants.currencies.map((currency) => (
                          <option key={currency.code} value={currency.code}>
                            {currency.code} - {translation.currencies?.[currency.code] || currency.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <fieldset className="space-y-3">
                      <legend className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        {translation.settings?.weightUnit || "Weight Unit"}
                      </legend>
                      <div className="flex space-x-6">
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="weightUnit"
                            value="gram"
                            checked={settings.weightUnit === "gram"}
                            onChange={(e) => handleSettingChange("weightUnit", e.target.value)}
                            className="mr-3 w-4 h-4 text-emerald-600 focus:ring-emerald-500 focus:ring-2"
                          />
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-emerald-600 transition-colors">
                            {translation.settings?.units?.gram || "Gram"}
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="weightUnit"
                            value="tola"
                            checked={settings.weightUnit === "tola"}
                            onChange={(e) => handleSettingChange("weightUnit", e.target.value)}
                            className="mr-3 w-4 h-4 text-emerald-600 focus:ring-emerald-500 focus:ring-2"
                          />
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-emerald-600 transition-colors">
                            {translation.settings?.units?.tola || "Tola"}
                          </span>
                        </label>
                      </div>
                    </fieldset>

                    <fieldset className="space-y-3">
                      <legend className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        {translation.settings?.nisabMethod || "Nisab Method"}
                      </legend>
                      <div className="flex space-x-6">
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="nisabMethod"
                            value="silver"
                            checked={settings.nisabMethod === "silver"}
                            onChange={(e) => handleSettingChange("nisabMethod", e.target.value)}
                            className="mr-3 w-4 h-4 text-emerald-600 focus:ring-emerald-500 focus:ring-2"
                          />
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-emerald-600 transition-colors">
                            {translation.settings?.silverNisab || "Silver"}
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="radio"
                            name="nisabMethod"
                            value="gold"
                            checked={settings.nisabMethod === "gold"}
                            onChange={(e) => handleSettingChange("nisabMethod", e.target.value)}
                            className="mr-3 w-4 h-4 text-emerald-600 focus:ring-emerald-500 focus:ring-2"
                          />
                          <span className="text-gray-700 dark:text-gray-300 group-hover:text-emerald-600 transition-colors">
                            {translation.settings?.goldNisab || "Gold"}
                          </span>
                        </label>
                      </div>
                    </fieldset>

                    <div>
                      <label
                        htmlFor="gold-price"
                        className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {/* gold price label with unit */}
                        { (translation.settings?.goldPriceLabel || "Gold Price (per {unit})").replace("{unit}", settings.weightUnit) }
                      </label>
                      <input
                        id="gold-price"
                        type="number"
                        value={settings.goldPrice}
                        onChange={(e) => handleSettingChange("goldPrice", e.target.value)}
                        className={`w-full p-4 border-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 transition-all duration-200 hover:border-emerald-300 ${
                          inputErrors.goldPrice
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 dark:border-gray-600 focus:border-emerald-500"
                        }`}
                        placeholder={ (translation.settings?.goldPricePlaceholder || "Gold price per {unit} in {currency}").replace("{unit}", settings.weightUnit).replace("{currency}", settings.currency) }
                      />
                      {inputErrors.goldPrice && (
                        <p className="mt-2 text-sm text-red flex items-center">
                          <FaExclamationTriangle className="mr-1" />
                          {inputErrors.goldPrice}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="silver-price"
                        className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                      >
                        {(translation.settings?.silverPriceLabel || "Silver Price (per {unit})").replace("{unit}", settings.weightUnit)}
                      </label>
                      <input
                        id="silver-price"
                        type="number"
                        value={settings.silverPrice}
                        onChange={(e) => handleSettingChange("silverPrice", e.target.value)}
                        className={`w-full p-4 border-2 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 transition-all duration-200 hover:border-emerald-300 ${
                          inputErrors.silverPrice
                            ? "border-red-500 focus:border-red-500"
                            : "border-gray-200 dark:border-gray-600 focus:border-emerald-500"
                        }`}
                        placeholder={ (translation.settings?.silverPricePlaceholder || "Silver price per {unit} in {currency}").replace("{unit}", settings.weightUnit).replace("{currency}", settings.currency) }
                      />
                      {inputErrors.silverPrice && (
                        <p className="mt-2 text-sm text-red flex items-center">
                          <FaExclamationTriangle className="mr-1" />
                          {inputErrors.silverPrice}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <FaWallet className="mr-3 text-emerald-600" aria-hidden="true" />
                    {translation.assets?.title || "Assets"}
                  </h2>

                  <div className="space-y-5">
                    {[
                      {
                        key: "cash",
                        icon: FaCoins,
                        label: translation.assets?.cashAndSavings || "Cash & Bank Balance",
                        placeholder: (translation.assets?.cashPlaceholder || "Enter amount in {currency}").replace("{currency}", settings.currency),
                      },
                      {
                        key: "gold",
                        icon: FaWeight,
                        label: (translation.assets?.goldSilver || "Gold ({unit})").replace("{unit}", settings.weightUnit),
                        placeholder: (translation.assets?.goldSilverPlaceholder || "Enter weight in {unit}").replace("{unit}", settings.weightUnit),
                      },
                      {
                        key: "silver",
                        icon: FaWeight,
                        label: (translation.assets?.goldSilver || "Silver ({unit})").replace("{unit}", settings.weightUnit),
                        placeholder: (translation.assets?.goldSilverPlaceholder || "Enter weight in {unit}").replace("{unit}", settings.weightUnit),
                      },
                      {
                        key: "investments",
                        icon: FaChartLine,
                        label: translation.assets?.investments || "Investments",
                        placeholder: (translation.assets?.investmentsPlaceholder || "Enter amount in {currency}").replace("{currency}", settings.currency),
                      },
                      {
                        key: "business",
                        icon: FaStore,
                        label: translation.assets?.businessAssets || "Business Inventory",
                        placeholder: (translation.assets?.businessPlaceholder || "Enter amount in {currency}").replace("{currency}", settings.currency),
                      },
                      {
                        key: "other",
                        icon: null,
                        label: translation.assets?.otherAssets || "Other Zakatable Assets",
                        placeholder: (translation.assets?.otherPlaceholder || "Enter amount in {currency}").replace("{currency}", settings.currency),
                      },
                    ].map(({ key, icon: Icon, label, placeholder }) => (
                      <div key={key}>
                        <label
                          htmlFor={`${key}-input`}
                          className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          {Icon && <Icon className="inline mr-2" aria-hidden="true" />}
                          {label}
                        </label>
                        <input
                          id={`${key}-input`}
                          type="number"
                          value={assets[key]}
                          onChange={(e) => handleAssetChange(key, e.target.value)}
                          className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 hover:border-emerald-300"
                          placeholder={placeholder}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-xl border border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <FaCreditCard className="mr-3 text-red-500" aria-hidden="true" />
                    {translation.liabilities?.title || "Liabilities"}
                  </h2>

                  <div>
                    <label
                      htmlFor="debts-input"
                      className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {translation.liabilities?.immediateDebts || "Immediate Debts (due within a year)"}
                    </label>
                    <input
                      id="debts-input"
                      type="number"
                      value={liabilities.debts}
                      onChange={(e) => handleLiabilityChange("debts", e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 hover:border-red-300"
                      placeholder={(translation.liabilities?.debtsPlaceholder || "Enter amount in {currency}").replace("{currency}", settings.currency)}
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-12 space-y-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={calculateZakat}
                  disabled={isCalculating}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:cursor-not-allowed"
                >
                  {isCalculating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      {translation.buttons?.calculating || "Calculating..."}
                    </>
                  ) : (
                    <>
                      <FaCalculator className="mr-3" aria-hidden="true" />
                      {translation.buttons?.calculate || "Calculate Zakat"}
                    </>
                  )}
                </button>
                <button
                  onClick={resetCalculator}
                  disabled={isCalculating}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:cursor-not-allowed"
                >
                  <FaRedo className="mr-3" aria-hidden="true" />
                  {translation.buttons?.reset || "Reset All"}
                </button>
              </div>
            </div>

            {showResult && (
              <section
                className="mt-12 space-y-6 animate-in slide-in-from-bottom-4 duration-700"
                aria-live="polite"
                aria-labelledby="results-heading"
              >
                <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-800 dark:via-emerald-900/30 dark:to-teal-900/30 rounded-2xl p-8 shadow-2xl border border-emerald-200 dark:border-emerald-700">
                  <h2
                    id="results-heading"
                    className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center"
                  >
                    <FaCalculator className="mr-4 text-emerald-600" aria-hidden="true" />
                    {translation.results?.title || "Calculation Results"}
                  </h2>

                  <div className="grid gap-6">
                    {[
                      { label: translation.results?.totalAssets || "Total Assets", value: results.totalAssets, description: null },
                      { label: translation.results?.netAssets || "Net Assets", value: results.netAssets, description: `(${translation.results?.netAssetsDescription || "Total Assets - Liabilities"})` },
                      {
                        label: translation.results?.nisabAmount || "Nisab Threshold",
                        value: results.nisabThreshold,
                        description: `${translation.results?.basedOn || "Based on"} ${settings.nisabMethod} ${translation.results?.standard || "standard"}`,
                      },
                    ].map(({ label, value, description }) => (
                      <div
                        key={label}
                        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 dark:text-gray-300 font-medium">{label}:</span>
                          <span className="font-bold text-xl text-gray-900 dark:text-white">
                            {formatCurrency(value)}
                          </span>
                        </div>
                        {description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">{description}</div>
                        )}
                      </div>
                    ))}

                    <div
                      className={`rounded-xl p-6 shadow-lg border-2 ${
                        results.isZakatDue
                          ? "bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/50 dark:to-teal-900/50 border-emerald-400 dark:border-emerald-600"
                          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 dark:text-gray-300 font-medium">{translation.results?.zakatDueLabel || "Zakat Due:"}</span>
                        <span
                          className={`font-bold text-2xl ${
                            results.isZakatDue
                              ? "text-emerald-700 dark:text-emerald-300"
                              : "text-gray-900 dark:text-white"
                          }`}
                        >
                          {formatCurrency(results.zakatDue)}
                        </span>
                      </div>
                      {results.isZakatDue && (
                        <div className="text-sm text-emerald-700 dark:text-emerald-300 mt-2 font-medium">
                          {translation.results?.zakatExplanation || "2.5% of net assets above nisab"}
                        </div>
                      )}
                    </div>

                    <div
                      className={`rounded-xl p-6 text-center shadow-lg border-2 ${
                        results.isZakatDue
                          ? "bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/50 dark:to-green-900/50 text-emerald-800 dark:text-emerald-200 border-emerald-400 dark:border-emerald-600"
                          : "bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/50 dark:to-orange-900/50 text-yellow-800 dark:text-yellow-200 border-yellow-400 dark:border-yellow-600"
                      }`}
                      role="status"
                      aria-live="assertive"
                    >
                      <div className="font-bold text-lg mb-2">
                        {results.isZakatDue ? (
                          <span className="flex items-center justify-center">
                            <FaCheckCircle className="mr-2" />
                            {translation.results?.zakatRequired || "Zakat is due on your wealth"}
                          </span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <FaExclamationTriangle className="mr-2" />
                            {translation.results?.zakatNotRequired || "No Zakat due (below nisab threshold)"}
                          </span>
                        )}
                      </div>
                      {!results.isZakatDue && results.nisabThreshold > 0 && (
                        <div className="text-sm">
                          { (translation.results?.belowNisab || "You need {amount} more to reach nisab").replace("{amount}", formatCurrency(results.nisabThreshold - results.netAssets)) }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </main>
    </>
  )
}

export default ZakatCalculator
